import React, { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { ABSpinner, useErrorService } from '@abroad/components'
import KeyTakeaways from './KeyTakeaways'
import ReviewAssessmentResults from './ReviewAssessmentResults'
import GoToNextStepBtn from './GoToNextStepBtn'
import API from '../../utils/API'

export const coachNotesKey = 'coachNotes'

const CategoriesTakeawaysForm = ({ coachNote, nextStepHandler, sessionId }) => {
  const { clientId, sowId, planId } = useParams()
  const [reviewCall, setReviewCall] = useState(null)
  const [coachNotes, setCoachNotes] = useState(coachNote)
  const [clientLogs, setClientLogs] = useState({ isLoading: true, data: null })
  const [surveyType, setSurveyType] = useState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isInitiallyLoad, setIsInitiallyLoad] = useState(true)
  const history = useHistory()
  const location = useLocation()
  const Error = useErrorService()
  const storageKey = `logs-${clientId}-${sowId}-${planId}`
  const logs = localStorage.getItem(storageKey)
  const parseValue = JSON.parse(logs)
  const isMatch =
    parseValue?.clientId === clientId && parseValue?.planId === planId
  useEffect(() => {
    if (isInitiallyLoad && isMatch && logs) {
      setCoachNotes(parseValue?.coachNotes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMatch, isInitiallyLoad])

  useEffect(() => {
    const getSurveyLogsAndSession = async () => {
      try {
        const { data: surveyData } =
          await API.AdminAPI.common.getClientSurveyLogs(
            clientId,
            sowId,
            planId === 'create' ? null : planId,
          )

        // If user has taken assessment (surveyType exists)
        if (surveyData?.surveyType) {
          setClientLogs({ isLoading: false, data: surveyData?.logs })
          setSurveyType(surveyData?.surveyType)

          // Extract values from logs structure if surveyLogs is not available
          let surveyAnswers = surveyData?.surveyLogs || {}

          // If surveyLogs is empty/null, extract from logs structure
          if (!surveyAnswers || Object.keys(surveyAnswers).length === 0) {
            surveyAnswers = {}
            const allLogs = [
              ...(surveyData?.logs?.awareness || []),
              ...(surveyData?.logs?.wisdom || []),
              ...(surveyData?.logs?.prosperity || []),
              ...(surveyData?.logs?.purposeAndLife || []),
            ]

            allLogs.forEach((log) => {
              if (log?.keyQuestions) {
                log.keyQuestions.forEach((question) => {
                  if (question?.name && question?.value) {
                    surveyAnswers[question.name] = question.value
                  }
                  // Also check stages
                  if (question?.stages) {
                    question.stages.forEach((stage) => {
                      if (stage?.name && stage?.value) {
                        surveyAnswers[stage.name] = stage.value
                      }
                    })
                  }
                })
              }
            })
          }

          if (isMatch && logs) {
            setReviewCall((prev) => ({
              ...prev,
              ...parseValue?.answers,
              ...surveyAnswers,
            }))
            setCoachNotes(parseValue?.coachNotes)
          } else {
            setReviewCall(surveyAnswers)
          }
        } else {
          // User hasn't taken assessment
          // If creating new, don't fetch session data
          if (planId === 'create') {
            setClientLogs({ isLoading: false, data: surveyData?.logs || null })
            setSurveyType(null)
            if (isMatch && logs) {
              setReviewCall(parseValue?.answers || {})
              setCoachNotes(parseValue?.coachNotes)
            } else {
              setReviewCall({})
            }
            return
          }

          // Get data from existing session
          const { data: sessionData } =
            await API.CoachAPI.coachingSession.getClientSession(
              planId,
              clientId,
            )

          // If logs exist, populate keyQuestions values with reviewCall data
          if (surveyData?.logs && sessionData?.reviewCall) {
            const updatedLogs = { ...surveyData.logs }

            // Update all log categories with reviewCall data
            Object.keys(updatedLogs).forEach((category) => {
              if (Array.isArray(updatedLogs[category])) {
                updatedLogs[category] = updatedLogs[category].map((item) => {
                  if (item?.keyQuestions) {
                    const updatedKeyQuestions = item.keyQuestions.map(
                      (question) => {
                        // If reviewCall has data for this question, use it
                        if (
                          question?.name &&
                          sessionData.reviewCall[question.name]
                        ) {
                          return {
                            ...question,
                            value: sessionData.reviewCall[question.name],
                          }
                        }
                        // Also check stages
                        if (question?.stages) {
                          const updatedStages = question.stages.map((stage) => {
                            if (
                              stage?.name &&
                              sessionData.reviewCall[stage.name]
                            ) {
                              return {
                                ...stage,
                                value: sessionData.reviewCall[stage.name],
                              }
                            }
                            return stage
                          })
                          return { ...question, stages: updatedStages }
                        }
                        return question
                      },
                    )
                    return { ...item, keyQuestions: updatedKeyQuestions }
                  }
                  return item
                })
              }
            })

            setClientLogs({ isLoading: false, data: updatedLogs })
          } else {
            setClientLogs({ isLoading: false, data: surveyData?.logs || null })
          }

          setSurveyType(null)

          // Use reviewCall data for form answers
          const surveyAnswers = sessionData?.reviewCall || {}

          if (isMatch && logs) {
            setReviewCall((prev) => ({
              ...prev,
              ...parseValue?.answers,
              ...surveyAnswers,
            }))
            setCoachNotes(parseValue?.coachNotes)
          } else {
            setReviewCall(surveyAnswers)
          }
        }
      } catch (e) {
        setClientLogs({ isLoading: false, data: null })
        Error.showError(e)
      }
    }
    getSurveyLogsAndSession()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (reviewCall || coachNotes) {
      if (logs && isInitiallyLoad) {
        setIsInitiallyLoad(false)
        return
      }
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          answers: reviewCall,
          coachNotes,
          clientId,
          sowId,
          planId,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewCall, coachNotes, sowId, clientId])

  const saveLogHandler = (data) => {
    if (reviewCall) {
      setReviewCall({ ...reviewCall, ...data })
    } else {
      setReviewCall({ ...data })
    }
  }

  const saveCoachNotesHandler = (data) => {
    setCoachNotes(data)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    const isValuesAreEmpty =
      reviewCall &&
      Object.values(reviewCall).every((e) => e === '' || e === null)
    try {
      const { data } = await API.CoachAPI.coachingSession.updateGrowthSession(
        clientId,
        sowId,
        {
          reviewCall: isValuesAreEmpty ? null : reviewCall,
          coachNotes: !coachNotes || coachNotes === '' ? '-' : coachNotes,
          ...(surveyType ? { surveyType } : {}),
          ...(sessionId !== 'create' && { sessionId }),
        },
      )
      if (data) {
        setIsSubmitting(false)
        nextStepHandler(
          isValuesAreEmpty ? null : reviewCall,
          !coachNotes || coachNotes === '' ? '-' : coachNotes,
          data?._id,
        )
        localStorage.removeItem(storageKey)
        if (planId === 'create') {
          history.replace(
            `/coach/clients/${clientId}/growth-plan/${sowId}/${data?._id}`,
            location.state,
          )
        }
      }
    } catch (e) {
      setIsSubmitting(false)
      Error.showError(e)
    }
  }

  if (clientLogs?.isLoading) {
    return <ABSpinner />
  }

  return (
    <section className='categories-takeaways h-100'>
      <Container fluid>
        <Row>
          <Col className='col-6 categories-notes'>
            <ReviewAssessmentResults
              logs={{ logs: clientLogs?.data }}
              sessionLogs={{ answers: reviewCall || {} }}
              saveLogHandler={saveLogHandler}
              disabled={isSubmitting}
              clientSurveyType={surveyType}
            />
          </Col>
          <Col className='col-6'>
            <KeyTakeaways
              qkey={coachNotesKey}
              key={coachNotesKey}
              saveLogHandler={saveCoachNotesHandler}
              answers={coachNotes}
              disabled={isSubmitting}
            />
            <div className='d-flex justify-content-end'>
              <GoToNextStepBtn
                nextStepHandler={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                btnClasses='mt-3 position-absolute growth-step-btn'
              />
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default CategoriesTakeawaysForm
